import {
  Activity,
  AppWindow,
  ArrowUpCircle,
  Award,
  DatabaseBackup,
  FileClock,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Menu,
  MonitorCog,
  ArrowLeftRight,
  Boxes,
  FileText,
  Puzzle,
  ScrollText,
  Settings,
  ShieldCheck,
  ShieldOff,
  SlidersHorizontal,
  Timer,
  X,
} from "lucide-react";
import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";

import { HealthPill } from "@/components/HealthPill";
import { useSession } from "@/hooks/useSession";
import { cn } from "@/lib/utils";

const NAV: { section: string; items: { to: string; label: string; icon: typeof LayoutDashboard; end?: boolean }[] }[] = [
  {
    section: "Protect",
    items: [
      { to: "/", label: "Overview", icon: LayoutDashboard, end: true },
      { to: "/applications", label: "Applications", icon: AppWindow },
      { to: "/exceptions", label: "Exceptions", icon: Timer },
      { to: "/bans", label: "Bans", icon: ShieldOff },
    ],
  },
  {
    section: "Observe",
    items: [
      { to: "/events", label: "Security Events", icon: ShieldCheck },
      { to: "/audit", label: "Audit Log", icon: ScrollText },
    ],
  },
  {
    section: "Operate",
    items: [
      { to: "/ops", label: "System", icon: MonitorCog },
      { to: "/jobs", label: "Jobs", icon: Activity },
      { to: "/plugins", label: "Plugins", icon: Puzzle },
      { to: "/modules", label: "Modules", icon: Boxes },
      { to: "/pages", label: "Pages", icon: FileText },
      { to: "/migration", label: "Migration", icon: ArrowLeftRight },
      { to: "/updates", label: "Updates", icon: ArrowUpCircle },
      { to: "/certificates", label: "Certificates", icon: Award },
      { to: "/backups", label: "Backups", icon: DatabaseBackup },
    ],
  },
  {
    section: "Configure",
    items: [
      { to: "/policies", label: "Policies", icon: SlidersHorizontal },
      { to: "/settings", label: "Settings", icon: Settings },
    ],
  },
];

const TITLES: [RegExp, string][] = [
  [/^\/$/, "Overview"],
  [/^\/applications\/[^/]+/, "Application detail"],
  [/^\/applications/, "Applications"],
  [/^\/exceptions/, "WAF Exceptions"],
  [/^\/bans/, "IP Bans"],
  [/^\/events/, "Security Events"],
  [/^\/audit/, "Audit Log"],
  [/^\/ops/, "System"],
  [/^\/jobs/, "Jobs"],
  [/^\/plugins/, "Plugins"],
  [/^\/updates/, "Updates"],
  [/^\/certificates/, "Certificates"],
  [/^\/backups/, "Backups"],
  [/^\/policies/, "Policies"],
  [/^\/settings/, "Settings"],
];

export default function Layout() {
  const { user, logout } = useSession();
  const { pathname } = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const title = TITLES.find(([re]) => re.test(pathname))?.[1] ?? "NXShield";
  const canOperate = user?.role === "owner" || user?.role === "operator";

  const sidebar = (
    <aside className="flex h-full w-60 flex-col border-r bg-card/40">
      <div className="flex h-14 items-center gap-2.5 border-b px-4">
        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/15">
          <ShieldCheck className="h-4 w-4 text-primary" />
        </div>
        <div>
          <div className="text-sm font-semibold leading-tight">NXShield</div>
          <div className="text-[10px] leading-tight text-muted-foreground">Security Console</div>
        </div>
      </div>
      <nav className="flex-1 space-y-4 overflow-y-auto p-3">
        {NAV.map((group) => (
          <div key={group.section}>
            <div className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
              {group.section}
            </div>
            <div className="space-y-0.5">
              {group.items.map(({ to, label, icon: Icon, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  onClick={() => setNavOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-2.5 rounded-md px-3 py-1.5 text-[13px] transition-colors",
                      isActive
                        ? "bg-accent font-medium text-foreground"
                        : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
                    )
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {label}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="border-t p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="truncate text-xs font-medium" title={user?.email}>
              {user?.name || user?.email}
            </div>
            <div className="mt-0.5">
              <span className={cn(
                "rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide",
                user?.role === "owner" && "bg-primary/15 text-primary",
                user?.role === "operator" && "bg-amber-500/15 text-amber-300",
                user?.role === "viewer" && "bg-muted text-muted-foreground",
              )}>
                {user?.role}
              </span>
            </div>
          </div>
          <button
            onClick={logout}
            title="Sign out"
            className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-3 flex items-center gap-1.5 text-[10px] text-muted-foreground/60">
          <FileClock className="h-3 w-3" /> upstream BunkerWeb 1.6.15
        </div>
      </div>
    </aside>
  );

  return (
    <div className="flex min-h-screen">
      {/* desktop sidebar */}
      <div className="hidden md:block">{sidebar}</div>
      {/* mobile drawer */}
      {navOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setNavOpen(false)} />
          <div className="absolute left-0 top-0 h-full">{sidebar}</div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        {/* topbar */}
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-3 border-b bg-background/85 px-4 backdrop-blur md:px-6">
          <div className="flex items-center gap-3">
            <button
              className="rounded p-1.5 text-muted-foreground hover:bg-accent md:hidden"
              onClick={() => setNavOpen(!navOpen)}
            >
              {navOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <span className="text-sm font-medium text-muted-foreground">{title}</span>
          </div>
          <div className="flex items-center gap-3">
            <HealthPill />
            {canOperate && (
              <span className="hidden items-center gap-1 text-[11px] text-muted-foreground sm:flex">
                <ListChecks className="h-3.5 w-3.5" /> operator
              </span>
            )}
          </div>
        </header>
        <main className="flex-1 overflow-auto">
          <div className="mx-auto max-w-[1400px] p-4 md:p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
